import { z } from 'zod';
import type {
  CatalogDimensions,
  EquipmentCategory,
  MuscleGroup,
  Product,
  ProductMuscleGroup,
  ProductVariant,
  Timestamp,
  UUID,
} from './entities.js';
import { httpsUrlSchema } from './validation.js';

export interface ManufacturerEquipmentSummary {
  id: UUID;
  manufacturer_id: UUID;
  slug: string;
  name: string;
  model: string | null;
  status: Product['status'];
  category_id: UUID | null;
  category_name: string | null;
  updated_at: Timestamp;
  total_count: number;
}
export interface ManufacturerEquipmentProduct extends Product {
  dimensions: CatalogDimensions;
  muscle_groups: ProductMuscleGroup[];
}
export interface ManufacturerEquipmentVariant extends ProductVariant {
  gtin: string | null;
  dimensions: CatalogDimensions;
}
/** Prices are decimal strings throughout the manufacturer editing workflow. */
export interface ManufacturerEquipmentOffer {
  id: UUID;
  product_id: UUID;
  variant_id: UUID | null;
  source_id: UUID | null;
  seller: string;
  region: string;
  currency: string;
  amount: string | null;
  availability: 'in_stock' | 'out_of_stock' | 'unavailable' | 'unknown' | 'stale';
  url: string;
  observed_at: Timestamp;
  offer_origin: 'catalog' | 'verified_manufacturer';
  verified_manufacturer_id: UUID | null;
  verified_at: Timestamp | null;
}
export interface ManufacturerEquipmentDetail {
  product: ManufacturerEquipmentProduct;
  variants: ManufacturerEquipmentVariant[];
  official_offers: ManufacturerEquipmentOffer[];
  other_offers: ManufacturerEquipmentOffer[];
  categories: EquipmentCategory[];
  muscle_groups: MuscleGroup[];
}

const status = z.enum(['draft', 'published', 'retired']);
const specificationObject = z.record(z.string(), z.unknown());
export const manufacturerDimensionsSchema = z
  .object({
    width_mm: z.number().positive().max(999999999.999).nullable().optional(),
    depth_mm: z.number().positive().max(999999999.999).nullable().optional(),
    height_mm: z.number().positive().max(999999999.999).nullable().optional(),
  })
  .strict();
export const manufacturerProductValuesSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    model: z.string().trim().max(160).nullable().optional(),
    category_id: z.uuid().nullable().optional(),
    description: z.string().max(20000).optional(),
    source_url: httpsUrlSchema.nullable().optional(),
    specifications: specificationObject.optional(),
    dimensions: manufacturerDimensionsSchema.optional(),
    muscle_groups: z
      .array(
        z
          .object({ slug: z.string().min(1).max(80), role: z.enum(['primary', 'secondary']) })
          .strict(),
      )
      .max(32)
      .optional(),
    status: status.optional(),
  })
  .strict();
export const manufacturerVariantValuesSchema = z
  .object({
    id: z.uuid(),
    name: z.string().trim().min(1).max(200).optional(),
    specifications: specificationObject.optional(),
    dimensions: manufacturerDimensionsSchema.optional(),
    status: status.optional(),
  })
  .strict();
export const manufacturerOfferValuesSchema = z
  .object({
    id: z.uuid().nullable(),
    variant_id: z.uuid().nullable(),
    region: z.string().regex(/^[A-Z]{2}$/),
    currency: z.string().regex(/^[A-Z]{3}$/),
    amount: z
      .string()
      .regex(/^(0|[1-9][0-9]{0,9})(\.[0-9]{1,2})?$/)
      .nullable(),
    availability: z.enum(['in_stock', 'out_of_stock', 'unavailable', 'unknown', 'stale']),
    url: httpsUrlSchema,
  })
  .strict();
export const manufacturerEquipmentSaveSchema = z
  .object({
    target_product: z.uuid(),
    product_values: manufacturerProductValuesSchema,
    variant_values: z.array(manufacturerVariantValuesSchema).max(100),
    offer_values: z.array(manufacturerOfferValuesSchema).max(100),
    reason: z.string().trim().min(10).max(2000),
  })
  .strict();
export type ManufacturerProductValues = z.infer<typeof manufacturerProductValuesSchema>;
export type ManufacturerVariantValues = z.infer<typeof manufacturerVariantValuesSchema>;
export type ManufacturerOfferValues = z.infer<typeof manufacturerOfferValuesSchema>;
export type ManufacturerEquipmentSave = z.infer<typeof manufacturerEquipmentSaveSchema>;
